
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY

// Check if user has signed out
const hasUserSignedOut = () => {
  try {
    return sessionStorage.getItem('user-signed-out') === 'true'
  } catch {
    return false
  }
}

// Custom storage implementation for better persistence
const customStorage = {
  getItem: (key: string) => {
    try {
      // Don't return auth tokens if user signed out
      if (hasUserSignedOut() && key.includes('auth')) {
        return null
      }
      return localStorage.getItem(key)
    } catch (error) {
      console.error('Error getting item from storage:', error)
      return null
    }
  },
  setItem: (key: string, value: string) => {
    try {
      // Don't store auth tokens if user signed out
      if (hasUserSignedOut() && key.includes('auth')) {
        return
      }
      
      localStorage.setItem(key, value)
      
      // Backup to sessionStorage for extra persistence
      if (key.includes('auth')) {
        sessionStorage.setItem(`backup_${key}`, value)
      }
    } catch (error) {
      console.error('Error setting item in storage:', error)
    }
  },
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key)
      sessionStorage.removeItem(`backup_${key}`)
    } catch (error) {
      console.error('Error removing item from storage:', error)
    }
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: customStorage,
    storageKey: 'sb-auth-token',
    flowType: 'pkce',
    debug: false
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Validate and refresh session if needed
export const validateSession = async () => {
  try {
    if (hasUserSignedOut()) {
      return null
    }

    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error || !session) {
      // Try to recover from backup
      const backupToken = sessionStorage.getItem('backup_sb-auth-token')
      if (backupToken && !hasUserSignedOut()) {
        localStorage.setItem('sb-auth-token', backupToken)
        const { data: { session: recoveredSession } } = await supabase.auth.getSession()
        return recoveredSession
      }
      return null
    }
    
    // Check if token needs refresh (within 5 minutes of expiry)
    const now = Math.floor(Date.now() / 1000)
    if (session.expires_at && session.expires_at < (now + 300)) {
      const { data: { session: refreshedSession } } = await supabase.auth.refreshSession()
      return refreshedSession
    }
    
    return session
  } catch (error) {
    console.error('Session validation failed:', error)
    return null
  }
}

// Force session refresh
export const forceSessionRefresh = async () => {
  try {
    if (hasUserSignedOut()) {
      return null
    }
    const { data: { session } } = await supabase.auth.refreshSession()
    return session
  } catch (error) {
    console.error('Force session refresh failed:', error)
    return null
  }
}

export type UserProfile = {
  id: string
  username: string | null
  avatar_url: string | null
  bio: string | null
  created_at: string
  updated_at: string
}
